// src/components/QuestionCard.tsx

import React, { useState } from 'react';
import { Question } from '../types';
import { motion } from 'framer-motion';
import { InfoPanel } from './InfoPanel';
import { InfoModal } from './InfoModal';

interface QuestionCardProps {
  question: Question;
  onAnswer: (questionId: string, value: string | string[]) => void;
  onBack: () => void;
  canGoBack: boolean;
  currentIndex: number;
  totalQuestions: number;
  isInfoOpen?: boolean;
  setIsInfoOpen?: (open: boolean) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onAnswer,
  onBack,
  canGoBack,
  currentIndex,
  totalQuestions,
  isInfoOpen: externalIsInfoOpen,
  setIsInfoOpen: externalSetIsInfoOpen,
}) => {
  const [selectedValue, setSelectedValue] = useState<string | string[] | null>(null);
  const [localIsInfoOpen, setLocalIsInfoOpen] = useState(false);
  const isMobile = window.innerWidth < 768;

  // Use external state if provided, otherwise use local state
  const isInfoOpen = externalIsInfoOpen !== undefined ? externalIsInfoOpen : localIsInfoOpen;
  const setIsInfoOpen = externalSetIsInfoOpen || setLocalIsInfoOpen;

  const handleSingleChoice = (value: string) => {
    setSelectedValue(value);
    // Auto-advance after 300ms
    setTimeout(() => {
      onAnswer(question.id, value);
      setSelectedValue(null);
    }, 300);
  };

  const handleMultipleChoice = (value: string) => {
    setSelectedValue(prev => {
      if (Array.isArray(prev)) {
        if (prev.includes(value)) {
          return prev.filter(v => v !== value);
        }
        return [...prev, value];
      }
      return [value];
    });
  };

  const handleMultipleSubmit = () => {
    if (selectedValue && Array.isArray(selectedValue) && selectedValue.length > 0) {
      onAnswer(question.id, selectedValue);
      setSelectedValue(null);
    }
  };

  const isSelected = (value: string): boolean => {
    if (Array.isArray(selectedValue)) {
      return selectedValue.includes(value);
    }
    return selectedValue === value;
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-xl p-8 mt-6">
        {/* Question Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-blue-600">
              Question {currentIndex} of {totalQuestions}
            </span>
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {question.section.toUpperCase()}
            </span>
          </div>
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 flex-1">
              {question.question}
            </h2>
            {question.tooltipContent && (
              <button
                onClick={() => setIsInfoOpen(true)}
                className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors flex items-center justify-center text-lg font-bold"
                title="Learn more about this question"
                aria-label={`More info: ${question.question}`}
              >
                ⓘ
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500">
            Regulation: {question.regulation}
          </p>
        </div>

      {/* Options */}
      <div className="space-y-3">
        {question.options.map((option, index) => (
          <motion.button
            key={option.value}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() =>
              question.type === 'multiple'
                ? handleMultipleChoice(option.value)
                : handleSingleChoice(option.value)
            }
            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
              isSelected(option.value)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-start">
              {question.type === 'multiple' && (
                <div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 mt-0.5 ${
                  isSelected(option.value)
                    ? 'bg-blue-500 border-blue-500'
                    : 'border-gray-300'
                }`}>
                  {isSelected(option.value) && (
                    <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium text-gray-900">{option.label}</div>
                {option.description && (
                  <div className="text-sm text-gray-500 mt-1">{option.description}</div>
                )}
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Multiple Choice Submit Button */}
      {question.type === 'multiple' && (
        <div className="mt-6 flex gap-3">
          {canGoBack && (
            <button
              onClick={onBack}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              ← Back
            </button>
          )}
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to start over? All progress will be lost.')) {
                window.location.href = '/';
              }
            }}
            className="px-6 py-3 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
          >
            🔄 Start Over
          </button>
          <button
            onClick={handleMultipleSubmit}
            disabled={!selectedValue || (Array.isArray(selectedValue) && selectedValue.length === 0)}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition ${
              selectedValue && Array.isArray(selectedValue) && selectedValue.length > 0
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue →
          </button>
        </div>
      )}

      {/* Single Choice Back Button */}
      {question.type === 'single' && canGoBack && (
        <div className="mt-6 flex gap-3">
          <button
            onClick={onBack}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            ← Back
          </button>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to start over? All progress will be lost.')) {
                window.location.href = '/';
              }
            }}
            className="px-6 py-3 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
          >
            🔄 Start Over
          </button>
        </div>
      )}
    </div>

    {/* Info Panel - Mobile only (desktop version rendered in parent) */}
    <div className="lg:hidden">
      {isMobile ? (
        <InfoModal
          question={question}
          isOpen={isInfoOpen}
          onClose={() => setIsInfoOpen(false)}
        />
      ) : (
        <InfoPanel
          question={question}
          isOpen={isInfoOpen}
          onClose={() => setIsInfoOpen(false)}
        />
      )}
    </div>
  </>
  );
};

export default QuestionCard;
